import type { ScopeActivationItem, ScopeItemRow } from "@/api/scope-activation";

export interface ScopeFilters {
  verdict?: string; // "" = todos
  lob?: string; // "" = todas
  search?: string; // casa scope_item_id (case-insensitive)
  confidence?: string; // "" = todas (alta/media/baixa)
}

/** Filtra itens por veredito, LoB, confiança e busca textual. Pura/testável. */
export function filterScopeItems(
  items: ScopeActivationItem[],
  f: ScopeFilters,
): ScopeActivationItem[] {
  const q = (f.search || "").trim().toLowerCase();
  return items.filter((i) => {
    if (f.verdict && i.verdict !== f.verdict) return false;
    if (f.lob && i.lob !== f.lob) return false;
    if (f.confidence && i.confidence !== f.confidence) return false;
    if (
      q &&
      !i.scope_item_id.toLowerCase().includes(q) &&
      !i.scope_item_name.toLowerCase().includes(q)
    )
      return false;
    return true;
  });
}

/** LoBs distintas (não vazias), ordenadas. */
export function distinctLobs(items: ScopeActivationItem[]): string[] {
  return Array.from(new Set(items.map((i) => i.lob).filter(Boolean))).sort();
}

const CSV_COLS: Array<[string, (i: ScopeActivationItem) => string]> = [
  ["scope_item_id", (i) => i.scope_item_id],
  ["scope_item_name", (i) => i.scope_item_name],
  ["lob", (i) => i.lob],
  ["verdict", (i) => i.verdict],
  ["confidence", (i) => i.confidence],
  ["clean_core", (i) => (i.clean_core ? "sim" : "")],
  ["alvo_no_public", (i) => i.s4_targets.join("; ")],
  ["triggered_by", (i) => i.triggered_by.join("; ")],
  ["capabilities", (i) => i.capabilities.join("; ")],
];

const CSV_DELIM = ";"; // Excel pt-BR usa ; como separador de lista

/** Matriz [cabeçalho, ...linhas] usada por CSV e Excel. Pura/testável. */
export function scopeItemsToMatrix(items: ScopeActivationItem[]): string[][] {
  const header = CSV_COLS.map(([h]) => h);
  const rows = items.map((i) => CSV_COLS.map(([, fn]) => fn(i)));
  return [header, ...rows];
}

/**
 * Começos que o Excel lê como fórmula. A mesma lista do XLSX do backend
 * (`_FORMULA_PREFIXES`, `artifact_tabulate.py`).
 */
export const FORMULA_PREFIXES: readonly string[] = ["=", "+", "-", "@", "\t", "\r"];

/**
 * Uma célula do CSV. Texto livre (a observação da curadoria, #1344) ganha um
 * apóstrofo na frente em dois casos: começa como fórmula (o Excel abre como
 * texto) ou já começa com apóstrofo literal (a carga remove exatamente uma
 * guarda, então o literal é duplicado); só depois vêm as aspas de quem tem `"`,
 * `;` ou quebra de linha.
 */
function esc(v: string): string {
  const text = v.startsWith("'") || FORMULA_PREFIXES.some((prefix) => v.startsWith(prefix))
    ? `'${v}`
    : v;
  return /[";\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function matrixToCsv(matrix: string[][]): string {
  return matrix.map((row) => row.map(esc).join(CSV_DELIM)).join("\n");
}

/** Serializa os itens em CSV (delimitador ";"). Pura/testável. */
export function scopeItemsToCsv(items: ScopeActivationItem[]): string {
  return matrixToCsv(scopeItemsToMatrix(items));
}

// --- CSV do banco (#1344) ----------------------------------------------------
// A linha do catálogo traz a decisão salva e a observação; a evidência do ERP
// (alvo, tabelas, capacidades) só existe no payload do run, e entra pelo id.

type RowColumn = (r: ScopeItemRow, evidence?: ScopeActivationItem) => string;

/** `null` sem nota, para a coluna não dizer "não" de uma nota que não existe. */
function yesNo(v: boolean | null | undefined): string {
  if (v == null) return "";
  return v ? "sim" : "não";
}

/** Colunas que o upload do Catálogo lê de volta: a chave e as duas editáveis. */
export const SHEET_ID_COLUMN = "scope_item_id";
export const SHEET_VERDICT_COLUMN = "verdict";
export const SHEET_NOTE_COLUMN = "note";
/** O "Fit (ERP)": só o download escreve; o upload a usa para reconhecer o próprio export. */
export const SHEET_FIT_COLUMN = "fit_erp";
/** Origem do veredito e data da nota: a curadoria muda as duas depois do download. */
export const SHEET_SOURCE_COLUMN = "verdict_source";
export const SHEET_NOTE_DATE_COLUMN = "note_updated_at";

// Na ordem das colunas da tela (Scope Item, LoB, Verdict, Fit (ERP), Confidence,
// Clean Core, Note), e depois as de apoio.
const ROW_COLS: Array<[string, RowColumn]> = [
  [SHEET_ID_COLUMN, (r) => r.scope_item_id],
  ["scope_item_name", (r) => r.scope_item_name ?? ""],
  ["lob", (r) => r.lob ?? ""],
  // O "Verdict" da tela: o veredito salvo, senão a sugestão.
  [SHEET_VERDICT_COLUMN, (r) => r.effective_verdict ?? ""],
  [SHEET_FIT_COLUMN, (r) => r.verdict],
  ["confidence", (r) => r.confidence ?? ""],
  ["clean_core", (r) => (r.clean_core ? "sim" : "")],
  // O "Note" da tela, com o texto inteiro.
  [SHEET_NOTE_COLUMN, (r) => r.curation_note ?? ""],
  ["show_in_report", (r) => yesNo(r.show_in_report)],
  // Vazio quando ninguém decidiu e vale a sugestão.
  [SHEET_SOURCE_COLUMN, (r) => r.decision_source ?? ""],
  ["suggestion", (r) => r.suggestion ?? ""],
  [SHEET_NOTE_DATE_COLUMN, (r) => r.note_updated_at ?? ""],
  ["alvo_no_public", (_r, e) => e?.s4_targets.join("; ") ?? ""],
  ["triggered_by", (_r, e) => e?.triggered_by.join("; ") ?? ""],
  ["capabilities", (_r, e) => e?.capabilities.join("; ") ?? ""],
];

/** Matriz [cabeçalho, ...linhas] das linhas do banco. Pura/testável. */
export function scopeItemRowsToMatrix(
  rows: ScopeItemRow[],
  evidenceById: Map<string, ScopeActivationItem>,
): string[][] {
  const header = ROW_COLS.map(([h]) => h);
  const body = rows.map((r) => {
    const evidence = evidenceById.get(r.scope_item_id);
    return ROW_COLS.map(([, fn]) => fn(r, evidence));
  });
  return [header, ...body];
}

/** Serializa as linhas do banco em CSV (delimitador ";"). Pura/testável. */
export function scopeItemRowsToCsv(
  rows: ScopeItemRow[],
  evidenceById: Map<string, ScopeActivationItem>,
): string {
  return matrixToCsv(scopeItemRowsToMatrix(rows, evidenceById));
}
